// The appearance decision without React: the choice cached on this device, what
// it resolves to, and applying it to <html>. ui/boot.ts runs these before the
// stylesheet paints, so the first frame already has the chosen theme (no dark
// flash for a light choice); ui/lib/theme.ts adds the React hooks on top. Both
// must agree, which is why there is one implementation.
import type { ThemeChoice } from '@/settings';

export type Theme = 'light' | 'dark';
export const THEME_KEY = 'poneva.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';
// The same three values as THEME_CHOICES in ui/settings.ts, stated here so the
// pre-paint script does not bundle the settings client.
const CHOICES: readonly ThemeChoice[] = ['light', 'dark', 'system'];

/**
 * The browser chrome colour for each theme: the workspace paper (`--ws-paper`,
 * DESIGN-SPEC D6), so the status bar and the page are one surface.
 */
export const THEME_COLOR: Readonly<Record<Theme, string>> = { light: '#faf9f7', dark: '#211f1c' };

/** This device's cached choice; follow the system when none. */
export function loadThemeChoice(): ThemeChoice {
  try {
    const value = localStorage.getItem(THEME_KEY);
    if (value && (CHOICES as readonly string[]).includes(value)) return value as ThemeChoice;
  } catch {
    /* private mode / storage disabled — use the default */
  }
  return 'system';
}

/** Only an explicit Light or Dark is stored; System is the absence of a choice. */
export function persistThemeChoice(choice: ThemeChoice) {
  try {
    if (choice === 'system') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, choice);
  } catch {
    /* best effort */
  }
}

export function darkQuery(): MediaQueryList | null {
  try {
    return typeof matchMedia === 'function' ? matchMedia(DARK_QUERY) : null;
  } catch {
    return null;
  }
}

export const systemPrefersDark = () => darkQuery()?.matches === true;

export const resolveTheme = (choice: ThemeChoice, systemDark: boolean): Theme =>
  choice === 'system' ? (systemDark ? 'dark' : 'light') : choice;

/**
 * The <html> class and the browser chrome colour. The stylesheet derives the
 * rest from the class: the palette, and `color-scheme`, which themes native
 * controls, scrollbars and form popups.
 */
export function applyThemeToDocument(theme: Theme) {
  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
}
